/**
 * Floating Chat Context
 *
 * Implements Option C: Hybrid Context-Aware Sessions
 *
 * Strategy:
 * - Single GLOBAL session persists across all page navigations
 * - Dynamic CONTEXT INJECTION based on current page
 * - Session history maintained while context updates per-page
 *
 * Industry Standard Implementation:
 * - Inspired by Intercom, Drift, and Bloomberg IB Chat patterns
 * - Session = continuous conversation with user
 * - Context = current page metadata injected as system context
 *
 * Benefits:
 * - User never loses chat history when navigating
 * - AI always knows current page context for relevant responses
 * - Single FloatingChatAssistant instance = better performance
 */

import { usePageContextMemory } from '../stores/pageContextMemoryStore';
import React, { createContext, useContext, useState, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { compactPageContext } from '../utils/contextCompaction';

// ==========================================
// TYPES
// ==========================================

/**
 * Page context passed to AI for contextual responses
 */
export interface PageContext {
  pageName: string;           // e.g., "Stock Research", "Portfolio Analysis"
  pageType: 'tool' | 'info' | 'dashboard' | 'landing' | 'chat';
  route: string;              // Current route path
  primaryAction?: string;     // What user can do here, e.g., "Research stocks"
  relevantData?: {            // Dynamic data from the page
    symbol?: string;          // If viewing a specific stock
    portfolioId?: string;     // If viewing a specific portfolio
    agentName?: string;       // If on an agent dashboard
    [key: string]: unknown;   // Extensible for page-specific data
  };
}

/**
 * Handle to control the floating chat programmatically
 */
export interface FloatingChatHandle {
  open: (view?: 'chat' | 'history' | 'actions') => void;
  close: () => void;
  toggle: () => void;
  isOpen: () => boolean;
}

/**
 * Context value exposed to consumers
 */
interface FloatingChatContextValue {
  // Page context management
  pageContext: PageContext;
  setPageContext: (context: Partial<PageContext>) => void;
  registerPageContext: (context: PageContext) => void;

  // Chat control
  chatRef: React.RefObject<FloatingChatHandle | null>;
  openChat: (view?: 'chat' | 'history' | 'actions') => void;
  closeChat: () => void;
  toggleChat: () => void;

  // Visibility
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;

  // Context injection for AI
  getContextSystemMessage: () => string;
  /**
   * The last TOOL-page context that named something — a stock, a portfolio —
   * kept across the route change to `/chat`, whose own default context has
   * nothing to say (ROADMAP "AI assistant — page context", part (a)). The
   * chat page shows it as a dismissible chip and sends it only while shown.
   */
  lastToolContext: PageContext | null;
  dismissLastToolContext: () => void;
}

// ==========================================
// CONTEXT
// ==========================================

const FloatingChatContext = createContext<FloatingChatContextValue | undefined>(undefined);

// ==========================================
// PAGE CONTEXT MAPPING
// ==========================================

/**
 * Maps routes to default page contexts
 * Pages can override with registerPageContext for dynamic data
 */
const getDefaultContextForRoute = (route: string): PageContext => {
  // Normalize route (remove trailing slashes, query params)
  const normalizedRoute = route.split('?')[0].replace(/\/$/, '') || '/';

  const routeContextMap: Record<string, PageContext> = {
    '/': {
      pageName: 'Home',
      pageType: 'landing',
      route: '/',
      primaryAction: 'Open the research and risk-analysis platform',
    },
    '/research': {
      pageName: 'Stock Research',
      pageType: 'tool',
      route: '/research',
      primaryAction: 'Research and analyze individual stocks',
    },
    '/chat': {
      pageName: 'AI Chat',
      pageType: 'chat',
      route: '/chat',
      primaryAction: 'Have a conversation about finance and investments',
    },
    '/portfolio/risk-assessment': {
      pageName: 'Risk Analysis Reports',
      pageType: 'tool',
      route: '/portfolio/risk-assessment',
      primaryAction: 'Open a past report or run a new analysis',
    },
    '/portfolio/risk-results': {
      pageName: 'Risk Analysis Results',
      pageType: 'dashboard',
      route: '/portfolio/risk-results',
      primaryAction: 'Review your portfolio risk analysis',
    },
    '/academy': {
      pageName: 'Academy',
      pageType: 'info',
      route: '/academy',
      primaryAction: 'Learn about investing concepts',
    },
    '/faq': {
      pageName: 'FAQ',
      pageType: 'info',
      route: '/faq',
      primaryAction: 'Get answers to common questions',
    },
    '/watchlist': {
      pageName: 'My Watchlist',
      pageType: 'tool',
      route: '/watchlist',
      primaryAction: 'Track your favorite stocks',
    },
    '/portfolios': {
      pageName: 'My Portfolios',
      pageType: 'tool',
      route: '/portfolios',
      primaryAction: 'Manage your investment portfolios',
    },
    '/profile': {
      pageName: 'Profile',
      pageType: 'info',
      route: '/profile',
      primaryAction: 'Manage your account settings',
    },
  };

  return routeContextMap[normalizedRoute] || {
    pageName: 'IndexAlpha',
    pageType: 'info',
    route: normalizedRoute,
    primaryAction: 'Explore financial insights',
  };
};

// ==========================================
// PROVIDER
// ==========================================

/** A context is worth carrying when it names a thing the reader was looking at. */
export function contextNamesSomething(ctx: PageContext | null | undefined): boolean {
  if (!ctx || ctx.pageType === 'chat') return false;
  const d = ctx.relevantData || {};
  return Boolean(d.symbol || d.portfolioId || d.companyName || d.portfolioName);
}

export const FloatingChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const chatRef = useRef<FloatingChatHandle | null>(null);

  // Initialize context based on current route
  const [pageContext, setPageContextState] = useState<PageContext>(() =>
    getDefaultContextForRoute(location.pathname)
  );

  // Visibility state (can hide on certain pages like /chat)
  const [isVisible, setIsVisible] = useState(true);
  // Held in a store, not here: this provider remounts on the way to /chat.
  const lastToolContext = usePageContextMemory((s) => s.lastToolContext);
  const rememberToolContext = usePageContextMemory((s) => s.remember);
  const dismissLastToolContext = usePageContextMemory((s) => s.forget);

  // Update context when route changes — clear stale relevantData
  React.useEffect(() => {
    const defaultContext = getDefaultContextForRoute(location.pathname);
    setPageContextState({
      ...defaultContext,
      relevantData: defaultContext.relevantData,
    });

    // Hide floating chat on routes where it'd be redundant or distracting:
    //   /chat          — the full chat UI already exists on this page
    //   /index_admin   — admin tooling; chat assistant has no role here
    //                    and the widget covers important data cells
    const hideOnRoutes = ['/chat', '/index_admin'];
    setIsVisible(!hideOnRoutes.includes(location.pathname));
  }, [location.pathname]);

  // Partial update of page context
  const setPageContext = useCallback((context: Partial<PageContext>) => {
    setPageContextState(prev => ({
      ...prev,
      ...context,
      relevantData: {
        ...prev.relevantData,
        ...context.relevantData,
      },
    }));
  }, []);

  // Full registration of page context (used by pages with dynamic data)
  const registerPageContext = useCallback((context: PageContext) => {
    setPageContextState(context);
  }, []);

  // Chat control methods
  const openChat = useCallback((view?: 'chat' | 'history' | 'actions') => {
    chatRef.current?.open(view);
  }, []);

  const closeChat = useCallback(() => {
    chatRef.current?.close();
  }, []);

  const toggleChat = useCallback(() => {
    chatRef.current?.toggle();
  }, []);

  // Generate compact system message for AI context injection
  // Uses pipe-delimited key=value format (~50% fewer tokens than prose)
  const getContextSystemMessage = useCallback((): string => {
    return compactPageContext(pageContext);
  }, [pageContext]);

  // Remember a context worth carrying. A chat-page default names nothing and
  // must never overwrite what the reader was just looking at; a tool page
  // that only has a route and a name is not worth a chip either.
  React.useEffect(() => {
    if (contextNamesSomething(pageContext)) {
      rememberToolContext(pageContext);
    }
  }, [pageContext, rememberToolContext]);

  const value = useMemo<FloatingChatContextValue>(() => ({
    pageContext,
    setPageContext,
    registerPageContext,
    chatRef,
    openChat,
    closeChat,
    toggleChat,
    isVisible,
    setIsVisible,
    getContextSystemMessage,
    lastToolContext,
    dismissLastToolContext,
  }), [
    pageContext,
    setPageContext,
    registerPageContext,
    openChat,
    closeChat,
    toggleChat,
    isVisible,
    getContextSystemMessage,
    lastToolContext,
    dismissLastToolContext,
  ]);

  return (
    <FloatingChatContext.Provider value={value}>
      {children}
    </FloatingChatContext.Provider>
  );
};

// ==========================================
// HOOKS
// ==========================================

/**
 * Hook to access floating chat context
 */
export const useFloatingChat = (): FloatingChatContextValue => {
  const context = useContext(FloatingChatContext);
  if (!context) {
    throw new Error('useFloatingChat must be used within a FloatingChatProvider');
  }
  return context;
};

/**
 * Hook for pages to register their context
 * Call this in pages with dynamic data (e.g., stock research with a symbol)
 *
 * @example
 * // In StockResearch component
 * usePageContext({
 *   pageName: 'Stock Research',
 *   pageType: 'tool',
 *   route: '/stock-research',
 *   primaryAction: 'Research stocks',
 *   relevantData: { symbol: currentSymbol }
 * });
 */
export const usePageContext = (context: PageContext): void => {
  const { registerPageContext } = useFloatingChat();

  React.useEffect(() => {
    registerPageContext(context);
  }, [context, registerPageContext]);
};

export default FloatingChatContext;
